import React, { useState, useEffect, useRef } from 'react';
import { Bell, Settings, LogOut, User, ChevronDown, X, AlertCircle, Info, MessageSquare, Package, Calendar, HelpCircle, Crown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '@/config/api';
import { TIERS } from '@/config/tiers';

interface Notification {
  id: string;
  type: 'order' | 'message' | 'appointment' | 'system';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

interface UserProfile {
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

export function EnhancedHeader({ title, showBackButton = false, backTo = "/", onGuidedSetupClick, userTier, onUpgradeClick }: {
  title: string;
  showBackButton?: boolean;
  backTo?: string;
  onGuidedSetupClick?: () => void;
  userTier?: string;
  onUpgradeClick?: () => void;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Settings state
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    theme: 'light'
  });
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);
  
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsedSettings }));
      } catch (_error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
  }, []);

  // Apply theme to document (only for dashboard pages)
  useEffect(() => {
    const root = document.documentElement;
    const currentPath = window.location.pathname;
    
    // Only apply theme to dashboard pages (customer, business, admin routes)
    const isDashboardPage = currentPath.startsWith('/customer/') || 
                           currentPath.startsWith('/business/') || 
                           currentPath.startsWith('/admin/');
    
    if (isDashboardPage) {
      if (settings.theme === 'dark') {
        root.classList.add('dark');
      } else if (settings.theme === 'light') {
        root.classList.remove('dark');
      } else {
        // System theme
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    } else {
      // For non-dashboard pages, always use light theme
      root.classList.remove('dark');
    }
  }, [settings.theme]);

  // Load real notifications from API
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        // No token - show empty notifications
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      // Debug logging
      console.log('🔍 EnhancedHeader Debug - Token:', token ? `${token.substring(0, 20)}...` : 'null');
      console.log('🔍 EnhancedHeader Debug - Full Token:', token);

      const response = await fetch(apiUrl('/api/notifications/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const realNotifications: Notification[] = (data.notifications || []).map((notif: any) => ({
          id: notif.id.toString(),
          type: notif.type || 'system',
          title: notif.title || 'Notification',
          message: notif.message || notif.content || 'You have a new notification',
          timestamp: notif.created_at || notif.timestamp || new Date().toISOString(),
          read: notif.read || false,
          actionUrl: notif.actionUrl || getActionUrl(notif.type, notif.data),
          data: notif.data
        }));
        
        setNotifications(realNotifications);
        setUnreadCount(realNotifications.filter(n => !n.read).length);
      } else {
        console.log('Notifications API failed, showing empty notifications');
        // No fallback to mock data - show empty notifications
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (_error) {
      console.log('Notifications fetch failed, showing empty notifications:', error);
      // No fallback to mock data - show empty notifications
      setNotifications([]);
      setUnreadCount(0);
    }
  };


  const getActionUrl = (type: string, data: any): string => {
    switch (type) {
      case 'order':
        return data?.order_id ? `/order/${data.order_id}/details` : '/customer/orders';
      case 'message':
        return '/customer/dashboard?tab=messaging';
      case 'appointment':
        return '/customer/dashboard?tab=appointments';
      case 'system':
        return '/customer/dashboard?tab=profile';
      default:
        return '/customer/dashboard';
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
  };

  const handleNotificationAction = async (notification: Notification) => {
    // Mark as read locally
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Mark as read on backend
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl(`/api/notifications/${notification.id}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.log('Failed to mark notification as read:', error);
    }
    
    // Navigate using the notification's action URL
    if (notification.actionUrl) {
      console.log('🔗 Navigating to notification URL:', notification.actionUrl);
      console.log('🔗 Notification data:', notification.data);
      console.log('🔗 Notification type:', notification.type);
      navigate(notification.actionUrl);
    } else {
      console.log('❌ No actionUrl found for notification:', notification);
    }
    
    setShowNotifications(false);
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    // Mark all as read on backend
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl('/api/notifications/mark-all-read'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (_error) {
      console.log('Failed to mark all notifications as read:', error);
    }
  };

  const handleUserMenuClick = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
    setShowUserMenu(false);
  };

  const handleLogoutClick = () => {
    logout();
    navigate('/');
  };

  // Settings handling functions
  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasSettingsChanges(true);

    // Handle push notification permission request
    if (key === 'pushNotifications' && value === true) {
      if ('Notification' in window) {
        if (Notification.permission === 'default') {
          Notification.requestPermission().then((permission) => {
            if (permission === 'denied') {
              setSettings(prev => ({ ...prev, pushNotifications: false }));
              alert('Push notifications have been disabled. Please enable them in your browser settings.');
            }
          });
        } else if (Notification.permission === 'denied') {
          setSettings(prev => ({ ...prev, pushNotifications: false }));
          alert('Push notifications are blocked. Please enable them in your browser settings.');
        }
      } else {
        setSettings(prev => ({ ...prev, pushNotifications: false }));
        alert('Push notifications are not supported in this browser.');
      }
    }
  };

  const handleSaveSettings = () => {
    try {
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setHasSettingsChanges(false);
      
      // Show success feedback
      const saveButton = document.querySelector('[data-save-settings]') as HTMLButtonElement;
      if (saveButton) {
        const originalText = saveButton.textContent;
        saveButton.textContent = 'Saved!';
        saveButton.classList.add('bg-green-600', 'hover:bg-green-700');
        saveButton.classList.remove('bg-blue-600', 'hover:bg-blue-700');
        
        setTimeout(() => {
          saveButton.textContent = originalText;
          saveButton.classList.remove('bg-green-600', 'hover:bg-green-700');
          saveButton.classList.add('bg-blue-600', 'hover:bg-blue-700');
        }, 2000);
      }
    } catch (_error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleCancelSettings = () => {
    // Reset to saved settings
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
      } catch (_error) {
        console.error('Failed to reset settings:', error);
      }
    }
    setHasSettingsChanges(false);
    setShowSettings(false);
  };

  const handleBackClick = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1); // Go back in browser history
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <Package className="w-4 h-4 text-blue-500" />;
      case 'message':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'appointment':
        return <Calendar className="w-4 h-4 text-purple-500" />;
      case 'system':
        return <Info className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return timestamp;
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Title and Back button */}
          <div className="flex items-center space-x-4">
            {showBackButton && (
              <button
                onClick={handleBackClick}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
          </div>

          {/* Right side - Icons */}
          <div className="flex items-center space-x-2">
            {/* Guided Setup Button */}
            <button
              onClick={() => {
                console.log('Guided Setup button clicked');
                console.log('onGuidedSetupClick:', onGuidedSetupClick);
                if (onGuidedSetupClick) {
                  console.log('Calling onGuidedSetupClick');
                  onGuidedSetupClick();
                } else if (window.guidedSetupCallback) {
                  console.log('Calling window.guidedSetupCallback');
                  window.guidedSetupCallback();
                } else {
                  console.log('No guided setup callback found');
                }
              }}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title="Guided Setup"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Guided Setup</span>
            </button>

            {/* Premium Plan Button - Merged Status and Upgrade */}
            {userTier && onUpgradeClick && (
              <button
                onClick={() => {
                  // Only trigger upgrade if not already on enterprise tier
                  if (userTier !== 'enterprise' && onUpgradeClick) {
                    onUpgradeClick();
                  }
                }}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                  userTier === 'enterprise'
                    ? 'bg-gray-100 text-gray-700 cursor-default'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title={userTier === 'enterprise' ? 'You are on the highest plan' : 'Upgrade Plan'}
                disabled={userTier === 'enterprise'}
              >
                <Crown className="w-4 h-4" />
                <span>
                  {TIERS[userTier]?.name || 'Free'} Plan
                  {userTier !== 'enterprise' && <span className="ml-1">↑</span>}
                </span>
              </button>
            )}

            {/* Notifications Bell */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={handleNotificationClick}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 min-w-[1.25rem] px-1 flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-slate-700">
                    <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">Notifications</span>
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                      disabled={unreadCount === 0}
                    >
                      Mark all as read
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-slate-400">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <button
                          key={n.id}
                          className={`w-full text-left px-4 py-3 flex items-start space-x-3 hover:bg-gray-50 dark:hover:bg-slate-700/40 ${!n.read ? 'bg-blue-50/40 dark:bg-slate-700/20' : ''}`}
                          onClick={() => handleNotificationAction(n as any)}
                        >
                          <div className="mt-0.5">
                            {getNotificationIcon(n.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{n.title}</p>
                              <span className="text-[11px] text-gray-500 dark:text-slate-400">{formatTimestamp(n.timestamp)}</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-slate-300 line-clamp-2">{n.message}</p>
                            {n.actionUrl && (
                              <span className="mt-1 inline-block text-xs text-blue-600">View</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Avatar with Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={handleUserMenuClick}
                className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
              >
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold shadow-md group-hover:shadow-lg transition-shadow">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-slate-100 transition-colors" />
              </button>

              {/* User Menu Dropdown */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                  <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-slate-100">{user?.name || 'User'}</p>
                        <p className="text-sm text-gray-600 dark:text-slate-400">{user?.email || 'user@example.com'}</p>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 mt-1">
                          {user?.role || 'Customer'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <button
                      onClick={() => {
                        // Navigate to appropriate profile based on user role
                        const profileRoute = user?.role === 'customer' ? '/customer/profile' : 
                                           user?.role === 'tailor' ? '/business/profile' : 
                                           user?.role === 'admin' ? '/admin/dashboard' : '/profile';
                        navigate(profileRoute);
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span>View Profile</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        // Navigate to appropriate notifications based on user role
                        const notificationsRoute = user?.role === 'customer' ? '/customer/notifications' : 
                                                 user?.role === 'tailor' ? '/business/dashboard' : 
                                                 user?.role === 'admin' ? '/admin/dashboard' : '/notifications';
                        navigate(notificationsRoute);
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                      data-tour="notifications-bell"
                    >
                      <Bell className="w-4 h-4" />
                      <span>All Notifications</span>
                    </button>
                    
                    <button
                      onClick={handleSettingsClick}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                      data-tour="user-settings"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </button>
                    
                    <div className="border-t border-gray-200 dark:border-slate-700 my-2"></div>
                    
                    <button
                      onClick={() => {
                        navigate('/contact');
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>Help & Support</span>
                    </button>
                    
                    <button
                      onClick={handleLogoutClick}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Email Notifications
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 dark:bg-slate-700"
                  />
                  <span className="text-sm text-gray-600 dark:text-slate-400">Receive email notifications</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Push Notifications
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.pushNotifications}
                    onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 dark:bg-slate-700"
                  />
                  <span className="text-sm text-gray-600 dark:text-slate-400">Receive push notifications</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Theme
                </label>
                <select 
                  value={settings.theme}
                  onChange={(e) => handleSettingChange('theme', e.target.value)}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:text-slate-100"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-slate-700">
              <button
                onClick={handleCancelSettings}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={!hasSettingsChanges}
                data-save-settings
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  hasSettingsChanges 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
